// PORTED COPY — A20 W2b. Original: TPRM7 packages/ui/src/reporter/useRecorder.ts
// (@vendorwatch/ui). The licence console installs the same rrweb 2.1.1 +
// console-record plugin so the two recorders emit the same event stream and one
// replay viewer serves both.
//
// ── THIS PORT HAS DRIFTED FROM THE ORIGINAL. READ THIS BEFORE TRUSTING IT ───
// This header used to say "identical below except for ONE deleted line" (a dead
// `prefer-rest-params` eslint directive). That stopped being true when the
// original was restructured into TWO LAYERS (A20 C2) and this copy was not.
//
// The difference is not cosmetic, and it costs diagnostic data:
//
//   · `consoleBuf` here is filled ONLY from rrweb console-plugin events inside
//     emit(). The original patches console.* synchronously, independently of
//     rrweb.
//   · `installNetworkShim` here is called only AFTER `import('rrweb')` resolves.
//     The original installs it before, and on surfaces where rrweb never loads
//     at all.
//
// So on this surface a failed or slow rrweb chunk costs the report its console
// lines AND its network lines, not just its replay — and the ticket arrives
// with "0 console · 0 requests", which is indistinguishable from a page that
// was quiet. Reports filed from the licence console are therefore strictly
// weaker than reports filed from the TPRM7 apps.
//
// A21·R2 brought across the buffer-preservation fix (below) because that one is
// small and the loss it prevents is silent. The two-layer restructure is the
// larger remaining job and is NOT done here. Do not describe these two files as
// in sync until it is.
//
// A20 · W1 — the rolling capture buffer.
//
// THE GOVERNING RULE: this must never break the host app. rrweb is loaded
// dynamically and every entry point is wrapped; any failure degrades to
// `captured: false` and the user still gets to file a text report. A reporter
// that crashes the page is worse than no reporter.
//
// WHY A RING BUFFER, NOT A RECORDING. Nothing is uploaded — or even retained
// beyond ~60s — until the user actually files. That is what keeps memory flat
// (the operator's explicit concern) and means we are not sitting on recordings
// of people who never reported anything.
//
// HOW THE WINDOW WORKS. rrweb events are only replayable from a FullSnapshot,
// so you cannot simply drop events older than N seconds — you would eventually
// drop the snapshot and the tail becomes unreplayable. rrweb's answer is
// `checkoutEveryNms`, which forces a fresh full snapshot on a cadence. We keep
// the last two checkout segments, so a report always starts at a snapshot and
// carries between one and two windows of context.

import * as React from 'react';
import type { ConsoleEntry, NetworkEntry, ReportCapture } from './types';
import { NO_CAPTURE_CLASS, MASK_CLASS, REVEAL_MASK_SELECTOR } from './types';

const DEFAULT_BUFFER_MS = 30_000;

/** Hard ceilings so a chatty page cannot grow the buffer without bound. */
/** rrweb event types (rrweb.js:11413,11415). Numeric: the enum is not imported. */
const RRWEB_TYPE_FULL_SNAPSHOT = 2;
const RRWEB_TYPE_META = 4;

const MAX_EVENTS_PER_SEGMENT = 8_000;
const MAX_CONSOLE = 200;
const MAX_NETWORK = 200;

type StopFn = () => void;

export interface RecorderState {
  /** True once rrweb is loaded and recording. */
  active: boolean;
  /** True when text is being masked (the default). */
  masked: boolean;
  /** Set when capture is unavailable; surfaced in the dialog, never thrown. */
  error: string | null;
}

export interface Recorder extends RecorderState {
  /** Snapshot the buffer for submission. Safe to call when inactive. */
  collect: () => ReportCapture;
  /**
   * Restart capture with a different masking posture.
   *
   * Masking is fixed at record() start — it is not a rendering flag — so
   * revealing text necessarily discards the masked buffer and starts a fresh
   * one. The dialog tells the user to reproduce the issue again; that is the
   * honest UX and the alternative (pretending we can unmask retroactively)
   * would be a lie.
   */
  restart: (opts: { masked: boolean }) => void;
}

/** Strip query + hash: they carry ring tokens (`?t=`) and reset tokens. */
function safeUrl(input: string): string {
  try {
    const u = new URL(input, typeof location !== 'undefined' ? location.href : 'http://localhost');
    return u.origin + u.pathname;
  } catch {
    return String(input).split('?')[0]!.split('#')[0]!;
  }
}

export function useRecorder(opts: {
  disabled?: boolean;
  bufferMs?: number;
  /** Studio mode records a longer window and may reveal text. */
  longWindow?: boolean;
}): Recorder {
  const { disabled = false, longWindow = false } = opts;
  const bufferMs = opts.bufferMs ?? (longWindow ? 5 * 60_000 : DEFAULT_BUFFER_MS);

  const [state, setState] = React.useState<RecorderState>({
    active: false,
    masked: true,
    error: null,
  });

  // Two checkout segments: the previous complete one and the one in progress.
  const prevSeg = React.useRef<unknown[]>([]);
  const currSeg = React.useRef<unknown[]>([]);
  const consoleBuf = React.useRef<ConsoleEntry[]>([]);
  const networkBuf = React.useRef<NetworkEntry[]>([]);
  const startedAt = React.useRef<number>(Date.now());
  const stopRec = React.useRef<StopFn | null>(null);
  const unpatch = React.useRef<StopFn | null>(null);
  // Bumped to force the effect to tear down and re-record with new masking.
  const [generation, setGeneration] = React.useState(0);
  const maskedRef = React.useRef(true);
  /**
   * Set by restart(), consumed by the effect: "this teardown is a masking
   * change, not a new capture session — keep the text buffers." (A21·R2; the
   * reasoning lives in the TPRM7 original, packages/ui/src/reporter/useRecorder.ts.)
   */
  const preserveTextBuffers = React.useRef(false);
  /** Checkout Meta held until its FullSnapshot arrives, so the pair rotates once. */
  const pendingMeta = React.useRef<unknown>(null);

  // ---------------------------------------------------------------------
  // Network shim. rrweb has no first-party network plugin (the official set is
  // console / sequential-id / canvas-webrtc), so this is ours. It rides the
  // supported `record.addCustomEvent` channel, which means it lands in the same
  // time-ordered stream as the DOM and replays in sync.
  //
  // Bodies are never captured. A TPRM response body is customer data.
  // ---------------------------------------------------------------------
  const installNetworkShim = React.useCallback((addCustomEvent?: (t: string, p: unknown) => void) => {
    if (typeof window === 'undefined') return null;

    const push = (entry: NetworkEntry) => {
      networkBuf.current.push(entry);
      if (networkBuf.current.length > MAX_NETWORK) networkBuf.current.shift();
      try {
        addCustomEvent?.('network', entry);
      } catch {
        /* custom events are a nicety; the parallel buffer is the source of truth */
      }
    };

    const origFetch = window.fetch;
    const origOpen = window.XMLHttpRequest?.prototype?.open;
    const origSend = window.XMLHttpRequest?.prototype?.send;

    if (typeof origFetch === 'function') {
      window.fetch = function patchedFetch(this: unknown, ...args: Parameters<typeof fetch>) {
        const t0 = Date.now();
        const req = args[0];
        const url = typeof req === 'string' ? req : req instanceof URL ? req.href : (req as Request)?.url ?? '';
        const method =
          (args[1]?.method ?? (req instanceof Request ? req.method : undefined) ?? 'GET').toUpperCase();
        return origFetch.apply(this as never, args).then(
          (res) => {
            push({ method, url: safeUrl(url), status: res.status, durationMs: Date.now() - t0, at: t0 - startedAt.current });
            return res;
          },
          (err) => {
            push({ method, url: safeUrl(url), failed: true, durationMs: Date.now() - t0, at: t0 - startedAt.current });
            throw err;
          },
        );
      } as typeof fetch;
    }

    if (origOpen && origSend) {
      const KEY = '__psReporter' as const;
      window.XMLHttpRequest.prototype.open = function patchedOpen(
        this: XMLHttpRequest & { [KEY]?: { method: string; url: string; t0: number } },
        method: string,
        url: string | URL,
        ...rest: unknown[]
      ) {
        this[KEY] = { method: String(method).toUpperCase(), url: String(url), t0: 0 };
        return origOpen.apply(this, [method, url, ...rest] as never);
      } as typeof origOpen;

      window.XMLHttpRequest.prototype.send = function patchedSend(
        this: XMLHttpRequest & { [KEY]?: { method: string; url: string; t0: number } },
        ...rest: unknown[]
      ) {
        const meta = this[KEY];
        if (meta) {
          meta.t0 = Date.now();
          this.addEventListener('loadend', () => {
            push({
              method: meta.method,
              url: safeUrl(meta.url),
              status: this.status || undefined,
              failed: this.status === 0,
              durationMs: Date.now() - meta.t0,
              at: meta.t0 - startedAt.current,
            });
          });
        }
        return origSend.apply(this, rest as never);
      } as typeof origSend;
    }

    return () => {
      if (typeof origFetch === 'function') window.fetch = origFetch;
      if (origOpen) window.XMLHttpRequest.prototype.open = origOpen;
      if (origSend) window.XMLHttpRequest.prototype.send = origSend;
    };
  }, []);

  React.useEffect(() => {
    if (disabled || typeof window === 'undefined') return;

    let cancelled = false;

    // Dynamic import so rrweb never blocks first paint. The buffer starts a few
    // hundred ms after hydration, which is well before a human notices a bug.
    (async () => {
      try {
        const [{ record }, consoleMod] = await Promise.all([
          import('rrweb'),
          import('@rrweb/rrweb-plugin-console-record').catch(() => null),
        ]);
        if (cancelled) return;

        startedAt.current = Date.now();
        prevSeg.current = [];
        currSeg.current = [];
        // A21·R2 — masking governs the DOM recording, not these. Wiping them on
        // a reveal toggle destroyed evidence for no privacy gain, and did it
        // while the dialog still displayed the pre-wipe counts.
        if (preserveTextBuffers.current) {
          preserveTextBuffers.current = false;
        } else {
          consoleBuf.current = [];
          networkBuf.current = [];
        }

        const plugins: unknown[] = [];
        if (consoleMod?.getRecordConsolePlugin) {
          plugins.push(
            consoleMod.getRecordConsolePlugin({
              level: ['info', 'log', 'warn', 'error'],
              lengthThreshold: 2000,
              stringifyOptions: { stringLengthLimit: 1000, numOfKeysLimit: 100, depthOfLimit: 4 },
            }),
          );
        }

        const masked = maskedRef.current;

        const stop = record({
          emit(event: unknown, isCheckout?: boolean) {
            // ── ONE CHECKOUT, ONE ROTATION (A21·R1a) ────────────────────────
            // rrweb's takeFullSnapshot emits TWO events per checkout and flags
            // BOTH with isCheckout — Meta (rrweb.js:14434) then FullSnapshot
            // (:14485). Rotating on each rotated twice, leaving prevSeg holding
            // one useless Meta and destroying the completed window: the real
            // retained window was whatever had accumulated since the last
            // checkout, not the two segments advertised. It still replayed,
            // which is why nothing caught it. Full reasoning lives in the TPRM7
            // original, packages/ui/src/reporter/useRecorder.ts.
            const type = (event as { type?: number }).type;
            if (isCheckout) {
              if (type === RRWEB_TYPE_META) {
                pendingMeta.current = event;
                return;
              }
              if (type === RRWEB_TYPE_FULL_SNAPSHOT) {
                prevSeg.current = currSeg.current;
                currSeg.current = pendingMeta.current ? [pendingMeta.current] : [];
                pendingMeta.current = null;
              }
            }
            currSeg.current.push(event);
            // Evict the oldest BODY event. `.shift()` removed index 0 — the
            // Meta, then the FullSnapshot — producing a stream no player can
            // start, while collect() still reported captured: true.
            if (currSeg.current.length > MAX_EVENTS_PER_SEGMENT) currSeg.current.splice(2, 1);

            // The console plugin emits into the same stream; mirror its payload
            // into a flat buffer so the ticket can show logs without a replay.
            const e = event as { type?: number; data?: { tag?: string; payload?: Record<string, unknown> }; timestamp?: number };
            if (e?.data?.tag === 'console' && e.data.payload) {
              const p = e.data.payload as { level?: string; payload?: string[]; trace?: string[] };
              consoleBuf.current.push({
                level: p.level ?? 'log',
                payload: Array.isArray(p.payload) ? p.payload.slice(0, 10) : [],
                trace: Array.isArray(p.trace) ? p.trace.slice(0, 10) : undefined,
                at: (e.timestamp ?? Date.now()) - startedAt.current,
              });
              if (consoleBuf.current.length > MAX_CONSOLE) consoleBuf.current.shift();
            }
          },
          checkoutEveryNms: bufferMs,
          // Inputs stay masked in BOTH postures. Revealing text is a consented
          // debugging aid; revealing a password field never is.
          maskAllInputs: true,
          // '*' masks every text node. In reveal mode the blanket is replaced —
          // NOT dropped — by the narrow selector, because dropping it left
          // `maskTextClass` as the only protection and that class had no
          // appliers anywhere: "include visible text" revealed everything,
          // including the contact details the consent copy promises to hold
          // back. See REVEAL_MASK_SELECTOR in types.ts for what it covers and,
          // more importantly, what it does not.
          maskTextSelector: masked ? '*' : REVEAL_MASK_SELECTOR,
          maskTextClass: MASK_CLASS,
          blockClass: NO_CAPTURE_CLASS,
          // Never inline cross-origin CSS — it can 404 loudly and adds weight.
          inlineStylesheet: true,
          collectFonts: false,
          recordCanvas: false,
          plugins: plugins as never,
        } as never);

        stopRec.current = typeof stop === 'function' ? (stop as StopFn) : null;
        unpatch.current = installNetworkShim(
          (record as unknown as { addCustomEvent?: (t: string, p: unknown) => void }).addCustomEvent?.bind(record),
        );

        setState({ active: true, masked, error: null });
      } catch (err) {
        if (cancelled) return;
        // Degrade, never throw. The user can still file a text-only report.
        setState({
          active: false,
          masked: maskedRef.current,
          error: err instanceof Error ? err.message : 'capture unavailable',
        });
      }
    })();

    return () => {
      cancelled = true;
      try {
        stopRec.current?.();
      } catch {
        /* ignore */
      }
      try {
        unpatch.current?.();
      } catch {
        /* ignore */
      }
      stopRec.current = null;
      unpatch.current = null;
    };
  }, [disabled, bufferMs, generation, installNetworkShim]);

  const collect = React.useCallback((): ReportCapture => {
    const events = [...prevSeg.current, ...currSeg.current];
    return {
      events,
      console: [...consoleBuf.current],
      network: [...networkBuf.current],
      durationMs: Date.now() - startedAt.current,
      captured: events.length > 0,
      masked: maskedRef.current,
    };
  }, []);

  const restart = React.useCallback((o: { masked: boolean }) => {
    maskedRef.current = o.masked;
    // The console and network buffers are not masked material (A21·R2).
    preserveTextBuffers.current = true;
    setState((s) => ({ ...s, active: false, masked: o.masked }));
    setGeneration((g) => g + 1);
  }, []);

  return { ...state, collect, restart };
}
